import React, { FC, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import ControlledTextField from 'transfix-form/components/ControlledTextInput'
import { Button } from 'transfix-ui/components/Button'
import { TToastNewState } from 'transfix-ui/components/Toast'
import { Box } from 'transfix-ui/core'
import * as yup from 'yup'
import { ResponsiveLayout } from 'apps/fleet-planner/components/layout/ResponsiveLayout'
import { ResponsiveLayoutRow } from 'apps/fleet-planner/components/layout/ResponsiveLayoutRow'
import * as Apis from 'apps/fleet-planner/utils/api'
import { VALIDATOR_PATTERNS } from 'utils/validators'

interface IPasswordForm {
  /**
   * Current password of the user
   */
  currentPassword: string

  /**
   * New password the user wants to set
   */
  newPassword: string
}

const CURRENT_PASSWORD_EMPTY_MESSAGE = 'Current Password is required.'
const CURRENT_PASSWORD_VALIDATION_MESSAGE = 'Invalid Password.'
const NEW_PASSWORD_VALIDATION_MESSAGE =
  'Pick an 8-character length password that has at least one uppercase letter, a lowercase letter, a number and a special character (!?@#$).'

const PasswordFormSchema = yup.object<IPasswordForm>({
  currentPassword: yup.string().required(CURRENT_PASSWORD_EMPTY_MESSAGE),
  newPassword: yup
    .string()
    .required()
    .matches(VALIDATOR_PATTERNS.PASSWORD, NEW_PASSWORD_VALIDATION_MESSAGE),
})

type TPasswordFormSchema = yup.InferType<typeof PasswordFormSchema>

interface IPasswordMobilePageProps {
  /**
   * A function provided by the parent component to navigate to the default page
   */
  onGoToDefaultRoute: () => void

  /**
   * A function provided by the parent component to open the toast on the main page
   */
  onOpenToast?: (options: TToastNewState) => void
}

export const PasswordMobilePage: FC<IPasswordMobilePageProps> = ({
  onGoToDefaultRoute,
  onOpenToast = () => {},
}) => {
  const {
    control,
    errors: formErrors,
    formState,
    getValues,
    handleSubmit,
    reset,
    setError,
    setValue,
  } = useForm({
    resolver: yupResolver(PasswordFormSchema),
    defaultValues: PasswordFormSchema.cast({
      currentPassword: '',
      newPassword: '',
    }),
  })

  const submitFormHandler = async (data: TPasswordFormSchema) => {
    const { currentPassword, newPassword } = data || {}
    try {
      await Apis.updateUserPassword({
        user: {
          currentPassword,
          password: newPassword,
          passwordConfirmation: newPassword,
        },
      })
      onGoToDefaultRoute()
      onOpenToast({
        toastType: 'default',
        toastMessage: 'Changes Saved.',
      })
    } catch (error) {
      setValue('currentPassword', '')
      setError('currentPassword', {
        type: 'string',
        message: CURRENT_PASSWORD_VALIDATION_MESSAGE,
      })
    }
  }

  // if the new password is invalid, we clear the input for the user
  useEffect(() => {
    if (formErrors?.newPassword) {
      const currentPassword = getValues('currentPassword')
      reset({ currentPassword, newPassword: '' }, { errors: true })
    }
  }, [formErrors?.newPassword, reset, getValues])

  const isDisabled = !formState.isDirty || formState.isSubmitting
  const submitHandler = handleSubmit(submitFormHandler)

  return (
    <ResponsiveLayout
      title='Change Password'
      onPressBackButton={onGoToDefaultRoute}
      footer={
        <Button
          data-test='submit-password'
          type='submit'
          color='primary'
          size='large'
          disabled={isDisabled}
          onClick={submitHandler}
          fullWidth>
          Change password
        </Button>
      }>
      <ResponsiveLayoutRow>
        <form onSubmit={submitHandler}>
          <ControlledTextField
            defaultValue=''
            control={control}
            name='currentPassword'
            TextInputProps={{
              dataTest: 'current-password',
              type: 'password',
              label: 'Current Password',
              placeholder: 'Current Password',
              error: !!formErrors?.currentPassword,
              validationMessage: formErrors?.currentPassword?.message,
              large: true,
              className: 'fs-exclude',
            }}
          />
          <Box pt={4}>
            <ControlledTextField
              defaultValue=''
              control={control}
              name='newPassword'
              TextInputProps={{
                dataTest: 'new-password',
                type: 'password',
                label: 'New Password',
                placeholder: 'New Password',
                error: !!formErrors?.newPassword,
                helperMessage: NEW_PASSWORD_VALIDATION_MESSAGE,
                validationMessage: formErrors?.newPassword?.message,
                large: true,
                className: 'fs-exclude',
              }}
            />
          </Box>
        </form>
      </ResponsiveLayoutRow>
    </ResponsiveLayout>
  )
}
